import { AlertColor, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel, FormModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { Privilege } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import IdsMappingKpiCards, { IdsMappingKind } from './IdsMappingKpiCards';
import { stackedIdsCell } from './idsMappingTableCells';

const sanitizeSearchFilter = (apiFilter: unknown): string[] => {
  if (!Array.isArray(apiFilter) || apiFilter.length === 0) {
    return [];
  }
  const match = apiFilter.find((token: unknown) => typeof token === 'string' && String(token).startsWith('search:'));
  return match ? [match] : [];
};

export type IdsMappingListConfig = {
  kind: IdsMappingKind;
  title: string;
  subtitle: string;
  addButtonText: string;
  addDrawerTitle: string;
  editDrawerTitle: string;
  searchPlaceholder: string;
  entityLabel: string;
  listApi: string;
  addApi: string;
  getById: (id: number) => string;
  saveApi: (id: number) => string;
  deleteApi: (id: number) => string;
  importApi: string;
  downloadImportTemplate: string;
  bbrHeader: string;
  idsHeader: string;
  idsSortField: string;
  getBbrLabel: (row: any) => string;
  getIdsName: (row: any) => string;
  getIdsCode: (row: any) => string;
  formModel: (dropdownOptions: any[]) => FormModel[];
  emptyFormValues: FormDataModel;
  loadDropdownOptions?: () => Promise<any[]>;
  mapResponseToForm: (data: any) => FormDataModel;
  mapFormToPayload: (body: FormDataModel) => Record<string, unknown>;
  getDeleteEntityName: (values: FormDataModel) => string;
};

const IdsMappingList: FC<{ config: IdsMappingListConfig }> = ({ config }) => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege) as string[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [dropdownOptions, setDropdownOptions] = useState<any[]>([]);
  const [kpiRefresh, setKpiRefresh] = useState(0);
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => config.formModel(dropdownOptions), [config, dropdownOptions]);

  const refreshKpis = () => setKpiRefresh(key => key + 1);

  const getList = useCallback(
    async (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : {
            page: index + 1,
            size: pageSize,
            filter: sanitizeSearchFilter(filter),
            sort: ['createdAt:desc'],
          };
      try {
        setLoading(true);
        const { data } = await axiosInstance.post(config.listApi, body);
        setPageResponse(data);
        if (filterData) {
          setKpiRefresh(key => key + 1);
        }
      } catch (error: any) {
        setPageResponse(error);
      } finally {
        setLoading(false);
      }
    },
    [config.listApi, index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  useEffect(() => {
    const load = config.loadDropdownOptions;
    if (!load) return;
    (async () => {
      try {
        const options = await load();
        setDropdownOptions(options || []);
      } catch {
        setDropdownOptions([]);
      }
    })();
  }, [config.loadDropdownOptions]);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      setDrawerOpen(true);
      setDrawerLoading(true);
      try {
        if (config.loadDropdownOptions && dropdownOptions.length === 0) {
          const options = (await config.loadDropdownOptions()) || [];
          setDropdownOptions(options);
        }
        if (recordId) {
          const { data } = await axiosInstance.get(config.getById(recordId));
          setFormValues(config.mapResponseToForm(data));
        } else {
          setFormValues({ ...config.emptyFormValues });
        }
      } catch (error: any) {
        handleCatch(error);
        setDrawerOpen(false);
        setEditingId(null);
      } finally {
        setDrawerLoading(false);
      }
    },
    [config, dropdownOptions.length],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const payload = config.mapFormToPayload(body);
      if (editingId) {
        await axiosInstance.put(config.saveApi(editingId), payload);
      } else {
        await axiosInstance.post(config.addApi, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      refreshKpis();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(config.deleteApi(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      refreshKpis();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: config.title,
      subtitle: config.subtitle,
      entityLabel: config.entityLabel,
      searchField: 'search',
      searchPlaceholder: config.searchPlaceholder,
      AddButtonText: config.addButtonText,
      listApi: config.listApi,
      deleteApi: config.deleteApi,
      addPrivilege: privileges.ids_configs,
      editPrivilege: privileges.ids_configs,
      deletePrivilege: privileges.ids_configs,
      importPrivilege: privileges.ids_configs,
      importIconOnly: true,
      importApi: config.importApi,
      downloadImportTemplate: config.downloadImportTemplate,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      renderKpis: () => <IdsMappingKpiCards currentType={config.kind} refreshKey={kpiRefresh} />,
      tableColumn: [
        {
          field: 'bbrName',
          headerName: config.bbrHeader,
          flex: 2,
          sortable: false,
          renderCell: (_value: any, row: any) => (
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35 }}>
              {config.getBbrLabel(row)}
            </Typography>
          ),
        },
        {
          field: config.idsSortField,
          headerName: config.idsHeader,
          flex: 3,
          sortable: true,
          renderCell: (_value: any, row: any) => stackedIdsCell(config.getIdsName(row), config.getIdsCode(row)),
        },
      ],
    }),
    [config, kpiRefresh, openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? config.editDrawerTitle : config.addDrawerTitle}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.ids_configs)}
        deleteEntityName={config.getDeleteEntityName(formValues)}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default IdsMappingList;
